import { ReactComponent as ArrowCircleRight } from 'img/topbar/arrow-circle-right.svg'
import { ReactComponent as NineDots } from 'img/topbar/nine-dots.svg'
import { ReactComponent as NotificationIcon } from 'img/topbar/notification-icon.svg'
import { ReactComponent as SettingsIcon } from 'img/icons/actions/settings-02.svg'

import React, {
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner } from '@shopify/polaris'
import UserMenuPortalPopover from 'components/UserMenuPortalPopover/UserMenuPortalPopover'
import CustomTooltip from 'components/CustomTooltip/CustomTooltip'
import useAppDispatch from 'hooks/useAppDispatch'
import useNotificationBadgeRoll from 'hooks/useNotificationBadgeRoll'
import { fetchNotificationCenterCounters } from '../../redux/features/notification-center/notificationCenterSlice'
import {
	formatNotificationCenterBadgeCount,
	getNotificationBadgeTone,
} from '../../containers/NotificationCenter/utils/notificationCenterCounts'
import { canAccessNotificationCenter } from '../../containers/NotificationCenter/utils/notificationCenterAccess'
import {
	isNotificationCenterPath,
	NOTIFICATION_CENTER_PATH,
} from '../../containers/NotificationCenter/utils/notificationCenterRefresh'
import { switchApp } from '../../redux/features/user/userSlice'
import { isLocalhost } from '../../utils/appIdStorage'
import { getProjectsForSwitcher } from '../../projectConfig/projects'
import { PRODUCT_SETTINGS_APPLICATIONS_PATH } from 'routes/shared/coreRoutes'
import './topbar.sass'

/** Set a number to override API badge count, or null to use API. */
const NOTIFICATION_BADGE_OVERRIDE = null

const PROFILE_SETTINGS_PATH_PREFIXES = [
	'/personal-settings',
	'/business-settings',
	'/global-settings',
	'/product-settings',
]

function isProfileSettingsPath(pathname) {
	return PROFILE_SETTINGS_PATH_PREFIXES.some((prefix) =>
		pathname.startsWith(prefix)
	)
}

const TopMenuSelectedProjects = () => {
	const { t } = useTranslation()
	const [showMenuChangeProjects, setShowMenuChangeProjects] = useState(false)
	const [switchingProject, setSwitchingProject] = useState(false)
	const toggleMenuChangeProjects = () => {
		setShowMenuChangeProjects((prev) => !prev)
	}
	const dispatch = useAppDispatch()
	const { openProjectName, user } = useSelector(state => state.user)
	const canSeeNotificationCenter = canAccessNotificationCenter(user)
	const notificationTotal = useSelector(
		(state) => state.notificationCenter?.counters?.total ?? 0
	)
	const countersHydrated = useSelector(
		(state) => state.notificationCenter?.countersHydrated === true
	)
	const menuRef = useRef(null)
	const navigation = useNavigate()
	const location = useLocation()
	const isOnNotificationCenterPage = isNotificationCenterPath(location.pathname)

	// Generate initials from user name and surname
	const getInitials = (firstName, lastName) => {
		const first = (firstName ?? '').trim()
		const last = (lastName ?? '').trim()
		
		if (!first && !last) {
			return '??'
		}
		
		const firstLetter = first?.at(0)?.toUpperCase() ?? ''
		const lastLetter = last?.at(0)?.toUpperCase() ?? ''
		
		// If we have both, return both letters
		if (firstLetter && lastLetter) {
			return `${firstLetter}${lastLetter}`
		}
		
		// If only one is available, duplicate it
		if (firstLetter) {
			return `${firstLetter}${firstLetter}`
		}
		
		if (lastLetter) {
			return `${lastLetter}${lastLetter}`
		}
		
		return '??'
	}

	// Generate light color based on user name (various neutral shades)
	const getAvatarColor = (firstName, lastName) => {
		// Light color palette with various neutral shades
		const lightColors = [
			'rgba(228, 243, 254, 1)', // Very light blue
			'rgba(214, 234, 252, 1)', // Light blue
			'rgba(240, 253, 244, 1)', // Very light green
			'rgba(220, 252, 231, 1)', // Light green
			'rgba(254, 242, 242, 1)', // Very light red/pink
			'rgba(254, 226, 226, 1)', // Light red/pink
			'rgba(255, 247, 237, 1)', // Very light orange
			'rgba(254, 243, 199, 1)', // Light orange/yellow
			'rgba(243, 244, 246, 1)', // Very light gray
			'rgba(229, 231, 235, 1)', // Light gray
			'rgba(237, 242, 247, 1)', // Very light slate
			'rgba(226, 232, 240, 1)', // Light slate
			'rgba(245, 243, 255, 1)', // Very light purple
			'rgba(233, 213, 255, 1)', // Light purple
			'rgba(236, 254, 255, 1)', // Very light cyan
			'rgba(207, 250, 254, 1)', // Light cyan
		]

		// Use combination of first and last name for color hash
		const fullName = `${firstName || ''} ${lastName || ''}`.trim()
		
		if (!fullName) {
			return lightColors[0] // Default light blue
		}

		// Simple hash function to get consistent color for same name
		let hash = 0
		for (let i = 0; i < fullName.length; i++) {
			hash = fullName.charCodeAt(i) + ((hash << 5) - hash)
		}
		
		const index = Math.abs(hash) % lightColors.length
		return lightColors[index]
	}

	const firstName = user?.first_name || user?.name || ''
	const lastName = user?.last_name || user?.surname || ''
	const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName])
	const avatarColor = useMemo(() => getAvatarColor(firstName, lastName), [firstName, lastName])
	const userName = `${firstName} ${lastName}`.trim() || 'User'

	// Get projects from centralized config
	const allProjects = useMemo(() => getProjectsForSwitcher(), [])

	const visibleProjects = useMemo(
			() =>
					allProjects.filter(project => {
						return project.name !== openProjectName
					}),
			[openProjectName, allProjects]
	)

	const notificationDisplayTotal =
		NOTIFICATION_BADGE_OVERRIDE != null
			? NOTIFICATION_BADGE_OVERRIDE
			: notificationTotal
	const useBadgeOverride = NOTIFICATION_BADGE_OVERRIDE != null

	const rolledBadge = useNotificationBadgeRoll(
		useBadgeOverride ? 0 : notificationTotal,
		{
			isReady: countersHydrated && !useBadgeOverride,
		}
	)

	const notificationBadge = useMemo(() => {
		if (useBadgeOverride) {
			return {
				text: formatNotificationCenterBadgeCount(notificationDisplayTotal),
				tone: getNotificationBadgeTone(notificationDisplayTotal),
				isRolling: false,
				displayCount: notificationDisplayTotal,
			}
		}

		return rolledBadge
	}, [notificationDisplayTotal, rolledBadge, useBadgeOverride])

	useEffect(() => {
		if (!canSeeNotificationCenter) {
			return
		}
		dispatch(fetchNotificationCenterCounters({ silent: true }))
	}, [canSeeNotificationCenter, dispatch])

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setShowMenuChangeProjects(false)
			}
		}

		if (showMenuChangeProjects) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showMenuChangeProjects])

	const marketplaceNavigationHandler = () => {
		navigation(PRODUCT_SETTINGS_APPLICATIONS_PATH)
	}

	const onProfileSettingsClick = () => {
		navigation('/personal-settings')
	}

	const onGlobalSettingsClick = () => {
		navigation('/global-settings')
	}

	const onNotificationsClick = () => {
		navigation(NOTIFICATION_CENTER_PATH)
	}

	const handleNotificationKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			onNotificationsClick()
		}
	}

	const handleSwitchProject = async (e, project) => {
		e.preventDefault()
		setShowMenuChangeProjects(false)
		setSwitchingProject(true)
		try {
			const result = await dispatch(switchApp(project.appId))
			if (result?.error) {
				setSwitchingProject(false)
				alert(result.error === 'Unauthorized' ? t('menu.sessionExpiredLoginAgain') : result.error)
				return
			}
			if (!isLocalhost()) {
				localStorage.setItem('app_id', project.appId.toString())
			}
			window.location.href = project.href
		} catch (err) {
			setSwitchingProject(false)
			const message = err?.payload || err?.message || t('menu.failedToSwitchApp')
			alert(message)
		}
	}

	return (
			<div className={`topbar-wrapper_block ${showMenuChangeProjects ? '' : ''}`}>
				{switchingProject && (
					<div
						style={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'grey',
							zIndex: 99999,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 16,
						}}
					>
						<div className="topbar-switching-spinner_white">
							<Spinner accessibilityLabel={t('menu.switchingAppLabel')} size="large" />
						</div>
						<span style={{ color: '#fff', fontSize: 16 }}>{t('menu.switchingApp')}</span>
					</div>
				)}
				{canSeeNotificationCenter ? (
					<CustomTooltip
						content={t('menu.notificationCenterTooltip')}
						preferredPosition='below'
						minWidth={80}
						maxWidth={200}
						tooltipStyle={{
							whiteSpace: 'nowrap',
							wordBreak: 'keep-all',
							overflowWrap: 'normal',
							wordWrap: 'normal',
						}}
					>
						<div
							className='topbar-wrapper_block-notification'
							onClick={onNotificationsClick}
							role='button'
							tabIndex={0}
							aria-label={
								notificationBadge.text
									? `${t('menu.notificationCenterTooltip')} (${notificationBadge.text})`
									: t('menu.notificationCenterTooltip')
							}
							onKeyDown={handleNotificationKeyDown}
						>
							<NotificationIcon
								className={
									isOnNotificationCenterPage
										? 'topbar-notification-icon topbar-notification-icon--active'
										: 'topbar-notification-icon'
								}
								style={{
									width: '24px',
									height: '24px',
								}}
								aria-hidden
							/>
							{notificationBadge.text ? (
								<span
									key={notificationBadge.text}
									className={`topbar-notification-badge topbar-notification-badge--${notificationBadge.tone}${
										notificationBadge.isRolling
											? ' topbar-notification-badge--is-rolling'
											: ''
									}${
										isOnNotificationCenterPage
											? ' topbar-notification-badge--active'
											: ''
									}`}
									aria-hidden='true'
								>
									{notificationBadge.text}
								</span>
							) : null}
						</div>
					</CustomTooltip>
				) : null}
				<CustomTooltip 
					content={t('menu.globalSettingsTooltip')}
					preferredPosition='below'
					minWidth={80}
					maxWidth={160}
					tooltipStyle={{ 
						whiteSpace: 'nowrap',
						wordBreak: 'keep-all',
						overflowWrap: 'normal',
						wordWrap: 'normal'
					}}
				>
					<div 
						className='topbar-wrapper_block-settings'
						onClick={onGlobalSettingsClick}
					>
						<SettingsIcon
							className={
								isProfileSettingsPath(location.pathname)
									? 'topbar-settings-icon topbar-settings-icon--active'
									: 'topbar-settings-icon'
							}
							style={{
								width: '24px',
								height: '24px',
							}}
							aria-hidden
						/>
					</div>
				</CustomTooltip>
				<CustomTooltip 
					content={t('menu.selectLovatApp')} 
					preferredPosition='below'
					minWidth={160}
					maxWidth={320}
					tooltipStyle={{ 
						whiteSpace: 'nowrap',
						wordBreak: 'keep-all',
						overflowWrap: 'normal',
						wordWrap: 'normal'
					}}
				>
					<div className={'topbar-wrapper_block-aura'}>
						<NineDots
								className={
									showMenuChangeProjects
										? 'topbar-wrapper_block-dot topbar-wrapper_block-dot--active'
										: 'topbar-wrapper_block-dot'
								}
								onClick={toggleMenuChangeProjects}
						/>
					</div >
				</CustomTooltip>
        <UserMenuPortalPopover
          initials={initials}
          userName={userName}
          onPersonalSettings={onProfileSettingsClick}
        />
				{
						showMenuChangeProjects &&
						<div
								className='topbar-wrapper_block-menu'
								ref={menuRef}
						>
							<div className='topbar-wrapper_block-menu_content'>
								<h3 className='topbar-wrapper_block-menu_title'>{t('menu.otherLovatApps')}</h3 >
								<ul className='topbar-wrapper_block-menu_body'>
									{visibleProjects.map((
											project,
											index
									) => (
											<li
													key={index}
													className='topbar-wrapper_block-menu_body-item'
											>
												<button
														type="button"
														className='topbar-wrapper_block-menu_body-item_link'
														style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
														onClick={(e) => handleSwitchProject(e, project)}
												>
													{React.createElement(project.icon, { className: 'topbar-wrapper_block-menu_body-item_link-icon' })}
													{project.title}
													<ArrowCircleRight className='topbar-wrapper_block-menu_body-item_link-icon_arrow' />
												</button>
											</li>
									))}
								</ul >
							</div >
							<div className='topbar-wrapper_block-menu_marketplace'>
								<button
										className='topbar-wrapper_block-menu_marketplace-button'
										onClick={marketplaceNavigationHandler}
								>
									{t('menu.manageApplications')}
								</button >
							</div >
						</div >
				}
			</div >
	)
}

export default TopMenuSelectedProjects